import { Commitment, Deal, Entity } from "@ifgengineering/client-invest-sdk";
import { reshapePortfolioData } from "./reshapePortfolioData";
import { DropDownValue } from "@components/DropDown/DropDown.component";
import { Currency, ExchangeRates } from "../types";

export const getByCurrency = (
  commitments: Commitment[],
  currency?: Currency
) => {
  if (!currency) return commitments;
  return commitments.filter((commitment) => commitment.currency === currency);
};

export const getByEntity = (commitments: Commitment[], entity?: string) => {
  if (entity === "all") return commitments;
  if (entity === "Personal")
    return commitments.filter((commitment) => !commitment.entity);
  if (entity === "IF-ISA")
    return commitments.filter((commitment) =>
      Boolean((commitment as any)?.ifIsaAccountId)
    );
  return commitments.filter((commitment) => commitment.entityId === entity);
};

export const getByAssetClasses = (
  commitments: Commitment[],
  deals: Deal[],
  assetClasses: string[]
) => {
  if (assetClasses.includes("all")) {
    return commitments;
  }

  return commitments.filter((commitment) => {
    const deal = deals.find((deal) => deal.id === commitment.dealId);
    const dealAssetClass = deal?.investable?.metadata?.assetClass;

    if (!dealAssetClass && assetClasses.includes("Others")) {
      return true;
    }

    return (
      dealAssetClass &&
      assetClasses.some(
        (assetClass) =>
          dealAssetClass.toLowerCase() === assetClass.toLowerCase()
      )
    );
  });
};

// TODO: implement when we have tax benefit data
export const getByTaxBenefit = (
  commitments: Commitment[],
  deals: Deal[],
  taxBenefit: string
) => {};

export const filterInvestments = ({
  commitments,
  deals,
  assetClasses = ["all"],
  exchangeRates,
  currency,
  entity = "all",
}: // taxBenefit = "all", TODO: add back in when we have tax benefit data
{
  commitments: Commitment[];
  deals: Deal[];
  assetClasses: string[];
  exchangeRates: ExchangeRates;
  currency?: Currency;
  entity?: "all" | "Personal" | "IF-ISA" | string;
}) => {
  const filteredByEntity = getByEntity(commitments, entity);
  const filteredByCurrency = getByCurrency(filteredByEntity, currency);
  const filteredByAssetClasses = getByAssetClasses(
    filteredByCurrency,
    deals,
    assetClasses
  );

  return reshapePortfolioData({
    commitments: filteredByAssetClasses,
    deals,
    exchangeRates,
    currency,
  });
};

export const getPortfolioEntityOptions = (
  commitments: Commitment[]
): DropDownValue[] => {
  const defaultCategories = [
    { label: "All Entities", value: "all" },
    { label: "Personal", value: "Personal" },
  ];

  return commitments.reduce((acc: DropDownValue[], commitment) => {
    const entity = commitment.entity as Entity;
    const hasEntityToShow = entity && !acc.some((e) => e.value === entity.id);
    const isIfIsa = Boolean(commitment.ifIsaAccountId);
    const hasIfIsaToShow = isIfIsa && !acc.some((e) => e.value === "IF-ISA");

    if (hasIfIsaToShow) {
      acc.push({ label: "IF-ISA", value: "IF-ISA" });
    }

    if (hasEntityToShow) {
      acc.push({ label: entity?.name, value: entity.id || "" });
    }

    return acc;
  }, defaultCategories);
};
